import { Calendar, Mail, Send, Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ExpandedContentType, SettingToggle } from '../settings-types'
import { DataRow, ToggleSwitch } from '@tendbot/ui'
import Badge from './Badge'

interface ConnectionExpandedProps {
  content: Extract<ExpandedContentType, { type: 'connection' }>
  toggles: Record<string, boolean>
  savingToggle: string | null
  onToggle: (toggleId: string, currentValue: boolean) => void
  onConnectGmail?: () => void
  onDisconnect: () => void
}

export default function ConnectionExpanded({ content, toggles, savingToggle, onToggle, onConnectGmail, onDisconnect }: ConnectionExpandedProps) {
  const { t } = useTranslation('settings')
  const { t: tc } = useTranslation('common')

  return (
    <>
      <div className="space-y-2">
        <DataRow label={t('connection.account')} value={content.account} />
        {content.connectedAt && <DataRow label={t('connection.connected')} value={content.connectedAt} />}
        <DataRow
          label={t('connection.status')}
          value={
            <span className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${
                content.service === 'Google Calendar' ? 'bg-blue-400'
                : content.service === 'Apple' ? 'bg-white'
                : 'bg-gold'
              }`} />
              <span className="text-emerald">{tc('status.active')}</span>
            </span>
          }
        />
      </div>

      {/* Service description for email connections */}
      {content.service === 'AI Email Identity' && (
        <div className="bg-charcoal/50 rounded-lg p-2.5">
          <div className="flex items-center gap-2 mb-1">
            <Send size={12} className="text-gold/50" />
            <span className="text-xs font-medium text-gold/50 uppercase tracking-wider">{t('connection.assistantAddress')}</span>
          </div>
          <p className="text-xs text-pearl/50 leading-relaxed">
            {t('connection.assistantAddressDesc')}
          </p>
        </div>
      )}

      {/* Calendars list */}
      {content.calendars && content.calendars.length > 0 && (
        <div>
          <span className="text-xs text-pearl/50 block mb-1.5">{t('connection.calendars')}</span>
          <div className="bg-charcoal/50 rounded-lg p-2.5 space-y-1.5">
            {content.calendars.map((cal) => (
              <div key={cal.id} className="flex items-center gap-2 text-xs">
                <Calendar size={12} className={cal.isPrimary ? 'text-gold' : 'text-pearl/60'} />
                <span className="text-pearl flex-1">{cal.name}</span>
                {cal.isPrimary && (
                  <span className="text-xs text-gold/60">{t('connection.primary')}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Gmail scope status */}
      {(content.service === 'Google Calendar' || content.service === 'Google Workspace') && (
        <div>
          <span className="text-xs text-pearl/50 block mb-1.5">{t('connection.services')}</span>
          <div className="bg-charcoal/50 rounded-lg p-2.5 space-y-1.5">
            <div className="flex items-center gap-2 text-xs">
              <Calendar size={12} className="text-gold" />
              <span className="text-pearl flex-1">{t('connection.calendarAccess')}</span>
              <Check size={12} className="text-emerald" />
            </div>
            <div className="flex items-center gap-2 text-xs">
              <Mail size={12} className={content.emailConnected ? 'text-gold' : 'text-pearl/55'} />
              <span className="text-pearl flex-1">{t('connection.gmailAccess')}</span>
              {content.emailConnected ? (
                <Check size={12} className="text-emerald" />
              ) : onConnectGmail ? (
                <button
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation()
                    onConnectGmail()
                  }}
                  className="text-xs text-gold hover:text-gold-light transition-colors"
                >
                  {tc('actions.connect')}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Settings toggles */}
      {content.settings?.map((setting: SettingToggle) => (
        <div key={setting.id} className="flex items-center justify-between py-2">
          <div className="flex items-center gap-2">
            <span className={`text-sm ${setting.badge ? 'text-pearl/60' : 'text-pearl'}`}>{setting.label}</span>
            {setting.badge && (
              <Badge>{setting.badge}</Badge>
            )}
          </div>
          {setting.type === 'toggle' && (
            <ToggleSwitch
              checked={(toggles[setting.id] ?? setting.value) as boolean}
              onChange={(e) => {
                e?.stopPropagation()
                if (!setting.badge) onToggle(setting.id, (toggles[setting.id] ?? setting.value) as boolean)
              }}
              disabled={!!setting.badge}
              saving={savingToggle === setting.id}
            />
          )}
        </div>
      ))}

      {/* Actions — AI email identity is system-provisioned, not disconnectable */}
      {content.service !== 'AI Email Identity' && (
        <div className="pt-2">
          <button
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation()
              onDisconnect()
            }}
            className="w-full py-2 rounded-lg bg-rose/10 text-rose text-xs font-medium hover:bg-rose/20 transition-colors"
          >
            {tc('actions.disconnect')}
          </button>
        </div>
      )}
    </>
  )
}
